import React, { useEffect, useState } from 'react'
import { array, bool, node, number, shape, string } from 'prop-types'
import queryString from 'query-string'
import { useNavigate, useLocation } from '@reach/router'

import { loadData } from '@jelly/utils'

export const SearchContext = React.createContext()

const searchTabs = {
	'': {
		label: 'All',
		slug: '',
	},
	article: {
		label: 'Articles',
		slug: 'article',
	},
	category: {
		label: 'Buying Guides',
		slug: 'category',
	},
	review: {
		label: 'Reviews',
		slug: 'review',
	},
	user: {
		label: 'Experts',
		slug: 'user',
	},
	question: {
		label: 'Advice',
		slug: 'question',
	},
	'infographic|whitepaper': {
		label: 'Downloads',
		slug: 'infographic|whitepaper',
	},
}

SearchProvider.propTypes = {
	businessListings: array,
	children: node.isRequired,
	deafultSearchTerm: string,
	mainContent: shape({
		content: array.isRequired,
		loadMore: bool.isRequired,
		skipInterval: number.isRequired,
	}),
	search: shape({
		total_result_count: number.isRequired,
	}),
	startTab: string, //.isRequired,
}

/**
 * This is the Search Provider, used to supply React Context values
 * controls and sets many properties used throughout the Search Page
 *
 */
function SearchProvider(props) {
	const {
		children,
		deafultSearchTerm,
		//mainContent: { content, loadMore, skipInterval } = {},
		//search: { total_result_count, num_pages } = {},
		startTab = '',
	} = props
	const location = useLocation()
	const navigate = useNavigate()

	const parseQuery = queryString.parse(location.search)
	const { d = `0`, q = deafultSearchTerm, s = `0` } = parseQuery // set default values for sort and filter

	// set major parameters based on query params or BE properties
	const [searchTerm, setSearchTerm] = useState(q)
	const [currentTab, setCurrentTab] = useState(startTab)
	const [results, setResults] = useState({})
	const [sort, setSort] = useState(s)
	const [filter, setFilter] = useState(d)

	// other app state variables
	const [nextPage, setNextPage] = useState(0) // use page so when we convert to pagination
	const [isLoadingPanelVisible, setIsLoadingPanelVisible] = useState(false)
	const [isLoadingMoreResults, setIsLoadingMoreResults] = useState(false) // set's More Content button to "Loading..."
	const [businessListings, setBusinessListings] = useState({ [searchTerm]: props.businessListings })

	/**
	 * handler for calling new data from swiftype api
	 * pass in corresponding parameters to get new payload
	 * cache results into the result state with existing search term and tab data
	 */
	const handleFetchNewData = ({
		searchTermValue = searchTerm,
		tabValue = currentTab,
		sortValue = sort,
		filterValue = filter,
	}) => {
		const newSearchQuery = `/search/?q=${encodeURI(searchTermValue)}&t=${tabValue}&s=${sortValue}&d=${filterValue}`
		setIsLoadingPanelVisible(true)

		loadData(`/api${newSearchQuery}`).then((payload) => {
			const newContent = payload.mainContent.content.filter((item) => item && item.canonical && item.canonical.indexOf('advice/member/p') === -1)
			const existingResultsData = results
			const existingResultsTabData = results[searchTermValue]
			setResults({
				...existingResultsData,
				[searchTermValue]: {
					...existingResultsTabData,
					[tabValue]: {
						data: newContent,
						loadMore: payload.mainContent.loadMore,
						nextPage: 2,
						totalCount: payload.search.total_result_count,
						totalPages: payload.search.num_pages,
					},
				},
			})
			setBusinessListings({ ...businessListings, [searchTermValue]: payload.businessListings })
			setIsLoadingPanelVisible(false)
		})

		setSearchTerm(searchTermValue)
		setCurrentTab(tabValue)
		setNextPage(2)
	}

	/**
	 * handlers for updating the history/query with search term
	 */
	const handleNewSearch = (newSearchTerm) => {
		const newSearchQuery = `/search/?q=${encodeURI(newSearchTerm)}&t=${currentTab}&s=${sort}&d=${filter}`
		navigate(newSearchQuery)
	}

	/**
	 * handlers for a more complicated state cache
	 * also note: these resets the state cache
	 */

	const handleLoadMoreResults = () => {
		const pageEndpoint = `/search/?q=${encodeURI(searchTerm)}&t=${currentTab}&s=${sort}&d=${filter}&p=${nextPage}`
		setIsLoadingMoreResults(true)

		loadData(`/api${pageEndpoint}`).then((payload) => {
			const newContent = payload.mainContent.content.filter((item) => item && item.canonical && item.canonical.indexOf('advice/member/p') === -1)
			const existingResultsData = results
			const existingTabResults = results[searchTerm][currentTab].data
			setResults({
				...existingResultsData,
				[searchTerm]: {
					[currentTab]: {
						data: [...existingTabResults, ...newContent],
						loadMore: payload.mainContent.loadMore,
						nextPage: nextPage + 1,
						totalCount: payload.search.total_result_count,
						totalPages: payload.search.num_pages,
					},
				},
			})
			setIsLoadingMoreResults(false)
		})

		setNextPage(nextPage + 1)
	}

	const handleSortFilter = (selected) => {
		const { id, value } = selected

		const newSearchQuery = `/search/?q=${encodeURI(searchTerm)}&t=${currentTab}&s=${
			id === 'sort' ? value : sort
		}&d=${id === 'filter' ? value : filter}`
		id === 'sort' ? setSort(value) : setFilter(value) // update the sort or filter state
		navigate(newSearchQuery)
		setIsLoadingPanelVisible(true)

		loadData(`/api${newSearchQuery}`).then((payload) => {
			const newContent = payload.mainContent.content.filter((item) => item && item.canonical && item.canonical.indexOf('advice/member/p') === -1)
			const existingResultsData = results
			setResults({
				...existingResultsData,
				[searchTerm]: {
					[currentTab]: {
						data: newContent,
						loadMore: payload.mainContent.loadMore,
						nextPage: 2,
						totalCount: payload.search.total_result_count,
						totalPages: payload.search.num_pages,
					},
				},
			})
			setNextPage(2)
			setIsLoadingPanelVisible(false)
		})
	}

	const { search } = location

	/**
	 * check the query string and parse the parameters
	 * based on query params checked the cached state, display if it's in the cached state
	 * call fetch function if the data doesn't exist in the cached state
	 * note: whenever the user loads more data from a tab, or sorts/fitlers the cached data state refreshes
	 */

	useEffect(() => {
		const parseNewLocation = queryString.parse(search)
		const { d, q, s, t } = parseNewLocation

		const sortUpdate = sort !== s
		const filterUpdate = filter !== d

		if (results[q] && results[q][t]) {
			if (sortUpdate) {
				handleSortFilter({ id: 'sort', value: s })
			} else if (filterUpdate) {
				handleSortFilter({ id: 'filter', value: d })
			} else {
				setCurrentTab(t)
				setSearchTerm(q)
				setSort(s)
				setFilter(d)
			}
		} else {
			handleFetchNewData({ filterValue: d, searchTermValue: q, sortValue: s, tabValue: t })
		}

		for (let index in searchTabs) {
			searchTabs[index].url = `/search/?q=${encodeURI(q)}&t=${searchTabs[index].slug}&s=${sort}&d=${filter}`
		}
	}, [search, sort, filter]) // eslint-disable-line

	return (
		<SearchContext.Provider
			value={{
				businessListings,
				currentTab,
				filter,
				navigate,
				handleLoadMoreResults,
				handleNewSearch,
				handleSortFilter,
				handleFetchNewData,
				isLoadingPanelVisible,
				isLoadingMoreResults,
				results,
				searchTerm,
				searchTabs,
				setIsLoadingPanelVisible,
				setCurrentTab,
				setNextPage,
				setResults,
				setSearchTerm,
				sort,
			}}
		>
			{children}
		</SearchContext.Provider>
	)
}

export default SearchProvider
